
import React, { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, Percent, Calendar, TrendingUp } from 'lucide-react';

const InvestmentCalculator: React.FC = () => {
  const [initial, setInitial] = useState(1000);
  const [monthly, setMonthly] = useState(250);
  const [years, setYears] = useState(15);
  const [rate, setRate] = useState(15);

  const data = useMemo(() => {
    let currentBalance = initial;
    const chartData = [];
    const monthlyRate = rate / 100 / 12;

    for (let year = 0; year <= years; year++) {
      chartData.push({
        year: `${year}a`,
        balance: Math.round(currentBalance)
      });
      for (let month = 0; month < 12; month++) {
        currentBalance = (currentBalance + monthly) * (1 + monthlyRate);
      }
    }
    return chartData;
  }, [initial, monthly, years, rate]);

  const finalBalance = data[data.length - 1].balance;

  return (
    <div className="max-w-6xl mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 uppercase font-oswald">
          TU <span className="text-gold">MÁQUINA</span> DE INTERÉS COMPUESTO
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto">
          Visualiza el poder de aplicar el método a largo plazo. Pequeñas victorias se convierten en fortunas.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        <div className="lg:col-span-4 glass-card p-8 rounded-[2rem] space-y-8">
          <div className="space-y-6">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Capital Inicial</label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gold w-5 h-5" />
                <input type="number" value={initial} onChange={(e) => setInitial(Number(e.target.value))} className="w-full bg-[#050517] text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 outline-none focus:border-gold transition-all font-bold text-lg" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Aporte Mensual</label>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gold w-5 h-5" />
                <input type="number" value={monthly} onChange={(e) => setMonthly(Number(e.target.value))} className="w-full bg-[#050517] text-white pl-12 pr-4 py-3 rounded-xl border border-white/10 outline-none focus:border-gold transition-all font-bold text-lg" />
              </div>
            </div>
            <div>
              <label className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                <span>Tiempo</span>
                <span className="text-white font-oswald text-sm">{years} años</span>
              </label>
              <input type="range" min="1" max="50" value={years} onChange={(e) => setYears(Number(e.target.value))} className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-gold" />
            </div>
            <div>
              <label className="flex justify-between text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">
                <span>Rendimiento Anual</span>
                <span className="text-white font-oswald text-sm">{rate}%</span>
              </label>
              <input type="range" min="1" max="30" value={rate} onChange={(e) => setRate(Number(e.target.value))} className="w-full h-1 bg-white/10 rounded-lg appearance-none cursor-pointer accent-gold" />
            </div>
          </div>

          <div className="pt-6 border-t border-white/5 text-center">
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-2 italic">Proyección Final</p>
            <div className="text-5xl font-black text-gold font-oswald tracking-tighter">${finalBalance.toLocaleString()}</div>
          </div>
        </div>

        <div className="lg:col-span-8 glass-card rounded-[2rem] p-6 border border-white/5 relative overflow-hidden flex flex-col">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <TrendingUp className="w-32 h-32 text-gold" />
          </div>
          <div className="flex-1 min-h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#fbbf24" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="year" stroke="#4b5563" fontSize={10} axisLine={false} tickLine={false} dy={10} />
                <YAxis hide />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1A1A4A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: '#fff', fontSize: '12px' }}
                  itemStyle={{ color: '#fbbf24' }}
                />
                <Area type="monotone" dataKey="balance" stroke="#fbbf24" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentCalculator;
